import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, FileText, Save, AlertCircle, CheckCircle } from 'lucide-react';
import Tooltip from '../../components/Tooltip/JobSeekerTooltip';
import VerificationGuard from '../../components/VerificationGuard';

const ScenarioTest: React.FC = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  
  const [currentScenario, setCurrentScenario] = useState(0);
  const [responses, setResponses] = useState<{[key: number]: string}>({});
  const [timeLeft, setTimeLeft] = useState(2700); // 45 minutes in seconds
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'saved' | 'saving' | 'error'>('saved');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [preventedActions, setPreventedActions] = useState(0);

  const mockScenarios = [
    {
      id: 1,
      title: 'Performance Optimization Challenge',
      description: 'Your React application is experiencing slow rendering and poor user experience.',
      scenario: `You're working on a large e-commerce application built with React. Users are complaining about slow page loads and laggy interactions, especially on the product listing page which displays hundreds of items. The page includes:

- A list of 500+ products with images, prices, and ratings
- Multiple filter options (category, price range, ratings)
- A search functionality
- Sorting options (price, popularity, ratings)

Your team lead has asked you to identify and fix the performance issues. The current implementation renders all products at once and re-renders the entire list when filters change.`,
      questions: [
        'What are the main performance issues you can identify in this scenario?',
        'What specific React optimization techniques would you implement to improve performance?',
        'How would you implement efficient filtering and searching without causing unnecessary re-renders?',
        'What tools would you use to measure and monitor the performance improvements?'
      ],
      timeLimit: 15,
      minWords: 200
    },
    {
      id: 2,
      title: 'State Management Architecture',
      description: 'Design a scalable state management solution for a complex application.',
      scenario: `You're building a collaborative project management tool similar to Trello/Asana. The application needs to handle:

- Multiple workspaces with different user permissions
- Real-time updates when team members make changes
- Offline functionality with sync when connection is restored
- Complex nested data structures (workspaces → projects → tasks → subtasks)
- User preferences and settings
- Notification system

The application will have multiple teams working on different features, and you need to ensure the state management solution is scalable and maintainable.`,
      questions: [
        'What state management approach would you choose and why?',
        'How would you structure the state to handle the complex nested data?',
        'How would you implement real-time updates and handle conflicts?',
        'What patterns would you use to ensure different teams can work independently?'
      ],
      timeLimit: 15,
      minWords: 250
    },
    {
      id: 3,
      title: 'Testing Strategy Implementation',
      description: 'Develop a comprehensive testing strategy for a React application.',
      scenario: `Your company is launching a critical financial application that handles user investments and transactions. The application includes:

- User authentication and authorization
- Complex forms for investment decisions
- Real-time data visualization (charts, graphs)
- Integration with multiple third-party APIs
- Mobile responsive design
- Accessibility compliance requirements

Due to the sensitive nature of financial data, the application must have comprehensive test coverage and cannot have any bugs in production. You need to establish a testing strategy that covers all aspects of the application.`,
      questions: [
        'What types of tests would you implement and what would each type cover?',
        'How would you test the integration with third-party APIs?',
        'What approach would you take for testing complex user interactions and forms?',
        'How would you ensure accessibility compliance through testing?'
      ],
      timeLimit: 15,
      minWords: 200
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleSubmitTest();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Copy-paste prevention and tab restrictions
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent common copy-paste shortcuts
      if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'v' || e.key === 'x' || e.key === 'a')) {
        e.preventDefault();
        setPreventedActions(prev => prev + 1);
        return false;
      }
      
      // Prevent F12 (DevTools), Ctrl+Shift+I, Ctrl+U, F5 (refresh)
      if (e.key === 'F12' || 
          ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'I') ||
          ((e.ctrlKey || e.metaKey) && e.key === 'u') ||
          e.key === 'F5' ||
          ((e.ctrlKey || e.metaKey) && e.key === 'r')) {
        e.preventDefault();
        setPreventedActions(prev => prev + 1);
        return false;
      }
    };

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      setPreventedActions(prev => prev + 1);
      return false;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        alert('Warning: Tab switching is not allowed during assessments. Please stay focused on the test.');
      }
    };

    const handleWindowFocus = () => {
      if (document.hidden) {
        alert('Warning: Please stay focused on the assessment. Switching tabs or windows is not permitted.');
      }
    };

    // Add event listeners
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('blur', handleWindowFocus);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('blur', handleWindowFocus);
    };
  }, []);

  // Full screen management
  useEffect(() => {
    const enterFullscreen = async () => {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
          setIsFullscreen(true);
        }
      } catch (error) {
      }
    };

    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(document.fullscreenElement);
      setIsFullscreen(isCurrentlyFullscreen);
      
      if (!isCurrentlyFullscreen && !isSubmitting) {
        alert('Warning: Please remain in fullscreen mode during the assessment for security purposes.');
        // Try to re-enter fullscreen
        enterFullscreen();
      }
    };

    // Enter fullscreen when component mounts
    enterFullscreen();
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      // Exit fullscreen when component unmounts
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, [isSubmitting]);

  // Auto-save functionality
  useEffect(() => {
    const autoSaveTimer = setTimeout(() => {
      if (responses[currentScenario] && responses[currentScenario].trim()) {
        setAutoSaveStatus('saving');
        // Simulate auto-save
        setTimeout(() => {
          setAutoSaveStatus('saved');
        }, 1000);
      }
    }, 2000);

    return () => clearTimeout(autoSaveTimer);
  }, [responses[currentScenario]]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleResponseChange = (value: string) => {
    setResponses(prev => ({
      ...prev,
      [currentScenario]: value
    }));
  };

  const handleNextScenario = () => {
    if (currentScenario < mockScenarios.length - 1) {
      setCurrentScenario(prev => prev + 1);
    }
  };

  const handlePreviousScenario = () => {
    if (currentScenario > 0) {
      setCurrentScenario(prev => prev - 1);
    }
  };

  const handleSubmitTest = async () => {
    setIsSubmitting(true);
    
    // Simulate test submission
    setTimeout(() => {
      navigate('/job-seeker/results');
    }, 2000);
  };

  const currentS = mockScenarios[currentScenario];
  const progress = ((currentScenario + 1) / mockScenarios.length) * 100;
  const wordCount = responses[currentScenario] ? responses[currentScenario].trim().split(/\s+/).length : 0;
  const completedScenarios = Object.keys(responses).filter(key => responses[parseInt(key)]?.trim()).length;

  if (isSubmitting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Submitting Your Responses</h2>
          <p className="text-gray-600">Please wait while we process your scenario responses...</p>
        </div>
      </div>
    );
  }

  return (
    <VerificationGuard requireVerification={true}>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Scenario-Based Assessment</h1>
            <p className="text-sm text-gray-600">React Developer Position - TechCorp Inc.</p>
          </div>
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-red-600" />
              <span className={`font-mono text-lg ${timeLeft < 300 ? 'text-red-600' : 'text-gray-900'}`}>
                {formatTime(timeLeft)}
              </span>
            </div>
            <div className="text-sm text-gray-600">
              Scenario {currentScenario + 1} of {mockScenarios.length}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Scenario Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sticky top-6">
              <h3 className="font-semibold text-gray-900 mb-4">Scenarios</h3>
              <div className="space-y-3">
                {mockScenarios.map((scenario, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentScenario(index)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      index === currentScenario
                        ? 'bg-red-800 text-white'
                        : responses[index]?.trim()
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <div className="font-medium text-sm">Scenario {index + 1}</div>
                    <div className="text-xs opacity-75 mt-1">{scenario.title}</div>
                  </button>
                ))}
              </div>
              
              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Progress</span>
                  <span className="font-medium">{Math.round(progress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-red-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <div className="text-xs text-gray-500">
                  {completedScenarios} of {mockScenarios.length} completed
                </div>
              </div>

              {/* Auto-save status */}
              <div className="mt-4 flex items-center space-x-2 text-xs">
                {autoSaveStatus === 'saved' && (
                  <>
                    <CheckCircle className="w-3 h-3 text-green-600" />
                    <span className="text-green-600">Auto-saved</span>
                  </>
                )}
                {autoSaveStatus === 'saving' && (
                  <>
                    <div className="w-3 h-3 border border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-blue-600">Saving...</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Scenario Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Scenario Description */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <span className="bg-red-100 text-red-800 text-sm px-3 py-1 rounded-full">
                  Scenario {currentScenario + 1}
                </span>
                <span className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                  {currentS.timeLimit} minutes
                </span>
              </div>
              
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                {currentS.title}
              </h2>
              
              <p className="text-gray-600 mb-4">{currentS.description}</p>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">Scenario Details:</h3>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {currentS.scenario}
                </p>
              </div>
            </div>

            {/* Questions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Questions to Address:</h3>
              <ul className="space-y-2 mb-6">
                {currentS.questions.map((question, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <span className="text-red-600 font-medium">{index + 1}.</span>
                    <span className="text-gray-700">{question}</span>
                  </li>
                ))}
              </ul>

              {/* Response Area */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Your Response
                  </label>
                  <div className="flex items-center space-x-4 text-sm">
                    <span className={`${
                      wordCount >= currentS.minWords ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      {wordCount} / {currentS.minWords} words minimum
                    </span>
                  </div>
                </div>
                
                <textarea
                  value={responses[currentScenario] || ''}
                  onChange={(e) => handleResponseChange(e.target.value)}
                  placeholder="Provide a detailed response addressing all the questions above. Be specific and include examples from your experience where relevant."
                  className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500 resize-none"
                  onCopy={(e) => {
                    e.preventDefault();
                    setPreventedActions(prev => prev + 1);
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    setPreventedActions(prev => prev + 1);
                  }}
                  onCut={(e) => {
                    e.preventDefault();
                    setPreventedActions(prev => prev + 1);
                  }}
                  onDragStart={(e) => e.preventDefault()}
                  onDrop={(e) => e.preventDefault()}
                  style={{ userSelect: 'none' }}
                />
                
                {wordCount < currentS.minWords && (
                  <div className="flex items-start space-x-2 text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <AlertCircle className="w-4 h-4 mt-0.5" />
                    <div>
                      <p className="font-medium">Minimum word count not met</p>
                      <p>Please provide at least {currentS.minWords} words for a comprehensive response.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {preventedActions > 0 && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <span className="text-red-800 text-sm font-medium">
                    Security Warning: {preventedActions} restricted action(s) detected. 
                    Copy-paste and right-click are disabled during assessments.
                  </span>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <Tooltip content="Go to previous scenario">
                <button
                  onClick={handlePreviousScenario}
                  disabled={currentScenario === 0}
                  className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous Scenario
                </button>
              </Tooltip>

              <div className="flex space-x-3">
                {currentScenario === mockScenarios.length - 1 ? (
                  <Tooltip content="Submit your completed assessment">
                    <button
                      onClick={handleSubmitTest}
                      className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Submit Assessment
                    </button>
                  </Tooltip>
                ) : (
                  <Tooltip content="Go to next scenario">
                    <button
                      onClick={handleNextScenario}
                      className="flex items-center px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors"
                    >
                      Next Scenario
                    </button>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </VerificationGuard>
  );
};

export default ScenarioTest;